import React, { useEffect, useState } from "react";
import FeatherIcon from "feather-icons-react";
import Button from "./button";
import PriceScopeChoice, { PRICE_SCOPE_OPEN } from "./price-scope-choice";

const MODULE_FIELDS = [
  { key: "eia", label: "EIA", placeholder: "250", defaultValue: 250 },
  { key: "eiaZon", label: "EIA Zon", placeholder: "250", defaultValue: 250 },
  { key: "isde", label: "ISDE", placeholder: "250", defaultValue: 250 },
  { key: "btwBatterij", label: "BTW Batterij (partner-prijs)", placeholder: "65", defaultValue: 65 },
];

/**
 * Update klant-kosten modal.
 *
 * Toont 4 inputs (één per aanvraag-module) met huidige bedragen pre-gevuld.
 * Submit stuurt `{ applicationFees: { eia, eiaZon, isde, btwBatterij } }`
 * naar de parent's onSubmit, met als tweede argument de `priceScope` als er
 * een prijs gewijzigd is (zie PriceScopeChoice).
 */
const AdminUpdateCustomerTaxModal = ({
  isOpen,
  onClose,
  onSubmit,
  applicationFees: applicationFeesProp,
  legacyApplicationFee,
}) => {
  // Lokale state als string voor input-handling (lege input = "" not 0).
  const [fees, setFees] = useState(() => makeInitialState({}));
  const [error, setError] = useState("");
  const [priceScope, setPriceScope] = useState(PRICE_SCOPE_OPEN);

  function makeInitialState(props) {
    return {
      eia: String(props?.eia ?? legacyApplicationFee ?? 250),
      eiaZon: String(props?.eiaZon ?? legacyApplicationFee ?? 250),
      isde: String(props?.isde ?? legacyApplicationFee ?? 250),
      btwBatterij: String(props?.btwBatterij ?? 65),
    };
  }

  useEffect(() => {
    setFees(makeInitialState(applicationFeesProp));
    setError("");
    setPriceScope(PRICE_SCOPE_OPEN);
    // eslint-disable-next-line
  }, [applicationFeesProp, legacyApplicationFee, isOpen]);

  useEffect(() => {
    const modal = document.getElementById("update-fees-modal");
    if (!modal) return;
    if (isOpen) {
      try { modal.showModal(); } catch { /* already open */ }
    } else if (modal.open) {
      modal.close();
    }
  }, [isOpen]);

  const handleClose = () => onClose();

  const initialFees = makeInitialState(applicationFeesProp);
  const priceChanged = MODULE_FIELDS.some(
    (f) => Number(fees[f.key]) !== Number(initialFees[f.key]),
  );

  const submitForm = () => {
    const parsed = {};
    for (const f of MODULE_FIELDS) {
      const val = fees[f.key];
      if (val === "" || val === null || val === undefined) {
        setError(`Vul ${f.label} in (€ excl. btw)`);
        return;
      }
      const num = Number(val);
      if (!Number.isFinite(num) || num < 0) {
        setError(`${f.label}: vul een geldig bedrag in`);
        return;
      }
      parsed[f.key] = num;
    }
    setError("");
    onSubmit(parsed, priceChanged ? priceScope : undefined);
    handleClose();
  };

  return (
    <dialog
      id="update-fees-modal"
      className="modal backdrop:bg-[#091E428A]"
      onCancel={(e) => { e.preventDefault(); handleClose(); }}
    >
      <div className="relative col-start-1 row-start-1 max-h-[calc(100vh-5em)] w-11/12 max-w-lg overflow-y-auto rounded-md bg-white text-ink shadow-overlay">
        <div className="modal-content">
          <button
            type="button"
            aria-label="Sluiten"
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded text-ink-subtlest hover:bg-[#091E420F]"
            onClick={handleClose}
          >
            <FeatherIcon icon="x" size={16} />
          </button>
          <h1 className="modal-title px-6 pt-6 text-xl font-medium text-ink">
            Update Klant Kosten
          </h1>
          <p className="px-6 pt-1 text-sm text-ink-subtlest">
            Bedragen zijn excl. btw, per aanvraag-module.
          </p>

          <form className="update-profile-form !mt-0 px-6 py-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {MODULE_FIELDS.map((f) => (
                <label key={f.key} className="block">
                  <span className="text-xs font-semibold text-ink-subtlest">
                    {f.label}
                  </span>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-sm text-ink-subtlest">&euro;</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={f.placeholder}
                      className="block w-full appearance-none !rounded !border-2 !border-solid !border-line !bg-white !px-2 !py-1.5 text-sm text-ink placeholder:text-ink-subtlest focus:!border-brand focus:outline-none"
                      value={fees[f.key]}
                      onChange={(e) =>
                        setFees((prev) => ({ ...prev, [f.key]: e.target.value }))
                      }
                    />
                  </div>
                </label>
              ))}
            </div>

            {priceChanged ? (
              <div className="mt-4">
                <PriceScopeChoice value={priceScope} onChange={setPriceScope} />
              </div>
            ) : null}

            <div className="mt-3 min-h-[16px]">
              {error ? (
                <p className="text-xs text-[#AE2A19]">{error}</p>
              ) : null}
            </div>

            <div className="mt-2 flex justify-end gap-2">
              <Button
                btnClassNames="h-8 rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover"
                text="Update Kosten"
                type="button"
                onClick={submitForm}
              />
            </div>
          </form>
        </div>
      </div>
    </dialog>
  );
};

export default AdminUpdateCustomerTaxModal;
